import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CustomTranslateService } from '@services/common/custom-translate.service';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, combineLatest, forkJoin, map, startWith, switchMap, takeUntil } from 'rxjs';
import { FormulaireCallCompletion } from 'app/@types/shared/formulaire-call-completion';

import naceData from '@assets/data/nace.json';
import { ToastService } from '@services/common/toast.service';
import { UserAction, UserState } from '@state/user.state';
import { UserClaims } from 'app/@types/dto/user-claims.model';
import { CognitoAuthenticationService } from 'app/auth/service';
import { CustomValidators } from 'app/main/helpers/custom-validators';
import { revealFormErrors } from 'app/main/helpers/form-validation';
import { FeatureService } from '@services/features/feature.service';
import { QuestionnaireState } from '@state/questionnaire.state';
import { QuestionnaireDto } from 'app/@types/dto/questionnaire-dto.model';
import { Country as CountryType } from 'app/@types/shared/world-cities';
import countryData from '@assets/data/countries.json';
import { UtilsService } from '@services/common/utils.service';
import { IndustryService } from '@services/common/industry.service';
import { IndustryDto } from 'app/@types/dto/industry-dto.model';
import { SelectChangeEvent } from 'primeng/select';
import { buildCountryOptions, CountryOption } from 'app/main/helpers/country-options';

export interface NaceCode {
    id: string;
    label: string;
    translationKey: string;
    industrySectorLabel: string;
    industrySectorId: string;
}

interface OrganizationSettingsFormControls {
    companyLogo: FormControl<string | null>;
    companyName: FormControl<string | null>;
    vatNumber: FormControl<string | null>;
    naceCode: FormControl<string | null>;
    industryCode: FormControl<string | null>;
    fte: FormControl<number | null>;
    country: FormControl<string | null>;
}

/**
 * The naceCode dropdown uses `optionValue="id"` and `filterBy="id,label"` (see template),
 * so the items it consumes are NaceCode objects (not PrimeNG SelectItems).
 * This local group type matches that runtime contract.
 */
interface NaceSelectGroup {
    label: string;
    value: string;
    items: NaceCode[];
}

@Component({
    selector: 'app-settings-form-organization',
    templateUrl: './settings-form-organization.component.html',
    styleUrls: ['./settings-form-organization.component.scss'],
    standalone: false,
})
export class SettingsFormOrganizationComponent implements OnInit, OnDestroy {
    @Select(UserState.getCurrentUserClaims) userClaims$!: Observable<UserClaims | undefined>;
    @Select(QuestionnaireState.getQuestionnaire) questionnaire$!: Observable<QuestionnaireDto>;

    public organizationForm: FormGroup<OrganizationSettingsFormControls>;
    public activities: NaceCode[] = [];
    public selectedIndustryActivity?: NaceCode;

    private ngUnsubscribe = new Subject<void>();
    public userClaim!: UserClaims;
    public questionnaire?: QuestionnaireDto;
    public loading: boolean = false;
    public countries: CountryOption[] = [];
    public isOrgOwner: boolean = false;
    public isInit: boolean = false;
    public isAdminForm: boolean = false;

    vatPlaceholder = 'BE0123.456.789';

    public industries?: IndustryDto[];
    private industries$ = this.industryService.get();

    groupedNace?: NaceSelectGroup[];

    constructor(
        private authService: CognitoAuthenticationService,
        private formBuilder: FormBuilder,
        private store: Store,
        private toastService: ToastService,
        private translateService: CustomTranslateService,
        private utilsService: UtilsService,
        private featureService: FeatureService,
        private readonly industryService: IndustryService,
        private readonly host: ElementRef<HTMLElement>,
    ) {
        this.organizationForm = new FormGroup<OrganizationSettingsFormControls>({
            companyLogo: new FormControl<string | null>(null),
            companyName: new FormControl<string | null>('', Validators.required),
            vatNumber: new FormControl<string | null>('', CustomValidators.countryVatValidator),
            naceCode: new FormControl<string | null>(''),
            industryCode: new FormControl<string | null>('', Validators.required),
            fte: new FormControl<number | null>(1, Validators.min(1)),
            country: new FormControl<string | null>(''),
        });
    }

    ngOnInit(): void {
        combineLatest([
            this.questionnaire$,
            this.userClaims$,
            this.translateService.onLangChange.pipe(startWith(null)),
            this.industries$,
        ])
            .pipe(
                takeUntil(this.ngUnsubscribe),

                switchMap(([quest, uc, lang, industries]) => {
                    // Create an array of observables for the translations
                    const translationObservables = naceData.map((item) => {
                        const labelTranslation$ = this.translateService.get(`nace.${item.translationKey}`);
                        const industrySectorLabelTranslation$ = this.translateService.get(
                            `components.settingsForm.industry.${item.industrySectorLabel}`,
                        );

                        // Use forkJoin to combine the two translations for each item
                        return forkJoin([labelTranslation$, industrySectorLabelTranslation$]).pipe(
                            map(([labelTranslation, industrySectorLabelTranslation]) => ({
                                ...item,
                                label: this.utilsService.handleTranslation(labelTranslation),
                                industrySectorLabel:
                                    this.utilsService.handleTranslation(industrySectorLabelTranslation),
                            })),
                        );
                    });

                    // Use forkJoin to wait for all translation observables to complete
                    return forkJoin(translationObservables).pipe(
                        map((translatedActivities) => ({
                            quest,
                            uc,
                            lang,
                            translatedActivities,
                            industries,
                        })),
                    );
                }),
            )
            .subscribe(({ quest, uc, lang: _lang, translatedActivities, industries }) => {
                this.activities = translatedActivities;

                this.industries = industries
                    .map((item) => ({
                        ...item,
                        label: this.translateService.instant(
                            `components.settingsForm.industry.${item.industrySectorLabel}`,
                        ),
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));
                this.translateService.get('countries').subscribe((countries) => {
                    this.countries = buildCountryOptions(countries, this.translateService.currentLang);
                });

                if (uc && uc.organization && quest) {
                    this.organizationForm.enable();
                    this.userClaim = uc;
                    if (uc.organization) {
                        this.isOrgOwner = uc.tasks.some(
                            (task) => task.name === 'orguser' && task.organization_id === uc.organization?.uid,
                        );
                    }
                    if (!this.isOrgOwner) {
                        this.organizationForm.disable();
                    }
                    this.questionnaire = quest;

                    //todo move this assignment to another callback or merge
                    [this.selectedIndustryActivity] = this.activities.filter(
                        (code) => code.industrySectorId === uc.organization?.industryId,
                    );
                    this.organizationForm.controls.companyLogo.setValue(uc.organization.avatar ?? null);
                    this.organizationForm.controls.companyName.setValue(uc.organization.name);
                    this.organizationForm.controls.vatNumber.setValue(uc.organization.tva ?? null);
                    this.organizationForm.controls.naceCode.setValue(uc.organization.nace ?? null);
                    this.organizationForm.controls.industryCode.setValue(uc.organization.industryId ?? null);
                    this.organizationForm.controls.country.setValue(uc.organization.country ?? null);
                    // Reflect the loaded country's VAT format in the hint (kept pristine, so a
                    // legacy-invalid number is shown but not flagged until the user edits it).
                    this.vatPlaceholder =
                        countryData.find((c: CountryType) => c.id === uc.organization?.country)?.placeholderVAT ??
                        'BE0123.456.789';
                    this.organizationForm.markAsPristine();
                    const [matching, nonMatching] = translatedActivities.reduce<[NaceCode[], NaceCode[]]>(
                        ([matches, nonMatches], nace) => {
                            if (nace.industrySectorId === uc.organization?.industryId) {
                                matches.push(nace);
                            } else {
                                nonMatches.push(nace);
                            }
                            return [matches, nonMatches];
                        },
                        [[], []],
                    );
                    // Sort both matching and non-matching arrays alphabetically
                    matching.sort((a, b) => a.label.localeCompare(b.label));
                    nonMatching.sort((a, b) => a.label.localeCompare(b.label));
                    this.groupedNace = [
                        {
                            label: 'Suggested',
                            value: 'suggested',
                            items: matching,
                        },
                        {
                            label: 'ALL',
                            value: 'all',
                            items: nonMatching,
                        },
                    ];
                } else {
                    this.organizationForm.disable();
                }
                this.isInit = true;
            });

        this.initializeFormValueSubscriptions();
    }

    onCountryChange(event: SelectChangeEvent) {
        if (event.value && event.value !== '') {
            const country: CountryType | undefined = countryData.find(
                (country: CountryType) => country.id === event.value,
            );
            const vatNumberControl = this.organizationForm.controls.vatNumber;
            // Keep the entered VAT and re-check it against the new country: mark it touched so a
            // number that is no longer valid surfaces its error (red outline + message) immediately.
            vatNumberControl.markAsTouched();
            vatNumberControl.updateValueAndValidity();
            this.vatPlaceholder = country?.placeholderVAT ?? 'BE0123.456.789';
        }
    }

    // Validate as soon as the user leaves the field (clicks away), not only after editing it.
    onVatBlur(): void {
        const vatNumberControl = this.organizationForm.controls.vatNumber;
        vatNumberControl.markAsTouched();
        vatNumberControl.updateValueAndValidity();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    public onSubmit() {
        // here are initiated the collection and the subject to handle navigation
        // the signals are added in the array of signals to listen if the condition is met below

        if (!revealFormErrors(this.organizationForm, this.host.nativeElement)) {
            return;
        }
        let questionnaireUpdateResult: Subject<FormulaireCallCompletion>;
        if (
            !this.isAdminForm &&
            this.userClaim?.organization &&
            this.organizationForm.valid &&
            !this.organizationForm.pristine
        ) {
            const org = this.userClaim.organization;
            const formControls = this.organizationForm.controls;
            org.avatar = formControls.companyLogo.value ?? undefined;
            org.name = formControls.companyName.value ?? '';
            org.tva = formControls.vatNumber.value ?? undefined;
            org.nace = formControls.naceCode.value ?? undefined;
            org.industryId = formControls.industryCode.value ?? undefined;
            org.isOnboarded = true;
            org.country = formControls.country.value ?? undefined;

            questionnaireUpdateResult = new Subject<FormulaireCallCompletion>();
            this.store.dispatch(new UserAction.UpdateOrganization(org, false, questionnaireUpdateResult));
            combineLatest([questionnaireUpdateResult])
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe({
                    next: (combo) => {
                        if (combo[0] === 'orgSuccess') {
                            this.toastService.showSuccess({
                                details: 'components.settingsForm.companySection.toasts.updateSuccess',
                            });
                        }
                        if (combo[0] === 'orgFail') {
                            this.toastService.showError({
                                details: 'components.settingsForm.companySection.toasts.updateError',
                            });
                        }
                    },
                });
        }
    }

    private initializeFormValueSubscriptions(): void {
        this.organizationForm.controls.industryCode.valueChanges.subscribe((selectedIndustry) => {
            if (selectedIndustry) {
                const [matching, nonMatching] = this.activities.reduce<[NaceCode[], NaceCode[]]>(
                    ([matches, nonMatches], nace) => {
                        if (nace.industrySectorId === selectedIndustry) {
                            matches.push(nace);
                        } else {
                            nonMatches.push(nace);
                        }
                        return [matches, nonMatches];
                    },
                    [[], []],
                );

                matching.sort((a, b) => a.label.localeCompare(b.label));
                nonMatching.sort((a, b) => a.label.localeCompare(b.label));

                this.groupedNace = [
                    {
                        label: 'Suggested',
                        value: 'suggested',
                        items: matching,
                    },
                    {
                        label: 'ALL',
                        value: 'all',
                        items: nonMatching,
                    },
                ];
            }
        });
    }
}
